import type { AssociateStatus } from '@/features/associate/types';
import { ASSIGNMENT_KIND } from '@/features/associate/types';

import { kioskFetch } from './kioskFetch';

/**
 * The status card, read from the api [KAS-1].
 *
 * ⚠️ **Hand-written where the convention (§ 6) asks for generated types**, for the same reason
 * `identityLookup.ts` records: `npm run api:types` reads `../iss-kiosk-api/openapi.json`, which the backend
 * does not emit yet. The shape mirrors `AssociateStatusResponseModel` field for field.
 */
export type AssociateStatusOutcome =
  /**
   * Every card the candidate has, and never an empty list — nothing to show is `none`.
   *
   * **One entry when they are placed, several when they have applied more than once** [Operator,
   * 2026-09-08]. The api decides which: a placement comes back alone (BR-K6), submissions come back in
   * full. The kiosk renders one card or a list of them and does not re-derive that rule.
   */
  | { readonly kind: 'cards'; readonly cards: readonly AssociateStatus[] }
  /**
   * Nothing to show HERE — a real answer rather than a failure.
   *
   * **It arrives as an empty `200`, and that is the common case, not the edge one** [decision 0185; the
   * api's `associate.controller.ts` states it in the endpoint description]. The list is scoped to this
   * kiosk's venue, so a candidate who applied from home or at another event has nothing to show at this
   * door — and *"sending them to find a representative would be wrong"*. The api's 404 branch was removed
   * when the scoping landed; `none` still covers a 404 because a boundary reads what it is sent, not what
   * it expects.
   *
   * The api deliberately does not say why: a candidate with no assignment and one whose submission is
   * barred get the identical response, because a screen in a public lobby must not distinguish them.
   */
  | { readonly kind: 'none' }
  /** The request never produced an answer. Routes to the same neutral hand-off as `none`. */
  | { readonly kind: 'unreachable' };

const REQUEST_TIMEOUT_MS = 8_000;

function apiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? '';
}

/** Validates at the boundary rather than trusting the api (§ 6). */
function readCard(body: unknown): AssociateStatus | undefined {
  if (typeof body !== 'object' || body === null) return undefined;
  const raw = body as Record<string, unknown>;

  const kind = raw.kind;
  if (kind !== ASSIGNMENT_KIND.placement && kind !== ASSIGNMENT_KIND.submission) return undefined;

  const strings = ['clientName', 'siteName', 'streetAddress', 'shift', 'date'] as const;
  for (const field of strings) if (typeof raw[field] !== 'string') return undefined;

  return {
    kind,
    clientName: raw.clientName as string,
    siteName: raw.siteName as string,
    streetAddress: raw.streetAddress as string,
    shift: raw.shift as string,
    date: raw.date as string,
    // Optional and additive [ISS-144]: the api that predates it sends no `dateValue`, and a card without one
    // is still a card — the screen falls back to the en-US `date`.
    ...(typeof raw.dateValue === 'string' ? { dateValue: raw.dateValue } : {}),
  };
}

/**
 * Fetches the cards. Never throws — every failure is a value, so the caller's job stays "put the candidate on
 * a working screen" rather than "catch an exception mid-render".
 *
 * ⭐ **The read is STILL venue-scoped, and the venue is now the credential's** [03-us-kad-6c; decisions
 * 0204, 0221]. It travelled as `venueId=PILOT_VENUE_ID` on the query string — the seam `lib/venue.ts` held
 * open *"until the PIN screen exists"*, which is this story — and the api now reads the venue off the
 * kiosk session cookie `kioskFetch` carries. ⛔ **Naming one on the request is a `400 property venueId
 * should not exist`**, so the parameter is gone rather than made optional: the scoping that decisions 0185
 * and 0194 require is unchanged, and only its carrier moved.
 *
 * ⚠️ The history is kept because it is the failure this endpoint already had once: the call sent no query
 * string at all, every request 400d, and the whole endpoint read as `unreachable` — which fails open, which
 * was the menu defect the Operator measured on 2026-09-10.
 */
export async function fetchAssociateStatus(candidateId: number): Promise<AssociateStatusOutcome> {
  let response: Response;
  try {
    response = await kioskFetch(`${apiBaseUrl()}/associate/${String(candidateId)}/status`, {
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { kind: 'unreachable' };
  }

  // Kept although the api no longer has a 404 branch: it answered that way until the venue scoping landed,
  // and a boundary that only understands the current deployment breaks against the previous one.
  if (response.status === 404) return { kind: 'none' };
  if (!response.ok) return { kind: 'unreachable' };

  const body: unknown = await response.json().catch(() => undefined);
  if (typeof body !== 'object' || body === null) return { kind: 'unreachable' };
  const raw = (body as Record<string, unknown>).cards;
  if (!Array.isArray(raw)) return { kind: 'unreachable' };

  /*
   * Every entry is validated, and one bad entry fails the whole response rather than being skipped. A
   * partially-read list would show a candidate two of their three applications with nothing saying so,
   * which is worse than the neutral hand-off `unreachable` produces.
   */
  const cards: AssociateStatus[] = [];
  for (const entry of raw) {
    const card = readCard(entry);
    if (!card) return { kind: 'unreachable' };
    cards.push(card);
  }

  /*
   * **An empty list is `none` — nothing to show here — and never `unreachable`** [decision 0185].
   *
   * It read `unreachable` until 2026-09-10, on the premise that the api 404s on empty and an empty list
   * therefore meant the contract had moved. The premise is gone: under venue scoping an empty `200` is
   * what the api sends to anyone whose applications are elsewhere, and the endpoint says so in as many
   * words. Reporting it as a failure made `ArrivalReasonScreen` hold `unknown`, which fails OPEN by
   * design — so a candidate with nothing on file here was offered an assignment to reconfirm that does
   * not exist, and an application list that routes to see-a-representative. Measured by the Operator in
   * live testing, 2026-09-10.
   *
   * `{ kind: 'cards', cards: [] }` is deliberately not a state: the union above promises `cards` is never
   * empty, so every consumer can read the first entry without asking.
   */
  return cards.length > 0 ? { kind: 'cards', cards } : { kind: 'none' };
}
